import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../helper/scriptHelpers';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';

Cookies.defaults = { sameSite: 'Lax' };

const SESSION_COOKIE = 'cw_d_session_info';
const EMBEDDED_SESSION_COOKIE = 'cw_d_session_info_embed';
const SESSION_COOKIE_ATTRIBUTES = { path: '/', sameSite: 'none', secure: true };
const EMBEDDED_SESSION_COOKIE_ATTRIBUTES = {
  ...SESSION_COOKIE_ATTRIBUTES,
  partitioned: true,
};

export const isEmbeddedContext = () => {
  try {
    return window.self !== window.top;
  } catch (error) {
    // Reading window.top throws when the parent is cross-origin
    return true;
  }
};

// Inside a cross-site iframe (the PhoneTrack monitoring embed) the session is
// stored under its own name and with the `Partitioned` attribute:
// - browsers that block third-party cookies drop any cookie written without
//   `Partitioned`, which left the embed unauthenticated on the reload that
//   follows the SSO login;
// - the dedicated name keeps the embed session apart from the one used by a
//   converx.app tab, so neither overwrites nor logs out the other.
export const getSessionCookieName = () =>
  isEmbeddedContext() ? EMBEDDED_SESSION_COOKIE : SESSION_COOKIE;

const getSessionCookieAttributes = () =>
  isEmbeddedContext()
    ? EMBEDDED_SESSION_COOKIE_ATTRIBUTES
    : SESSION_COOKIE_ATTRIBUTES;

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  window.bus.$emit(CHATWOOT_SET_USER, { user });
  window.bus.$emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  Cookies.set(getSessionCookieName(), response.headers, {
    ...getSessionCookieAttributes(),
    expires: differenceInDays(expiryDate, new Date()),
  });
  setUser(response.data.data, expiryDate);
};

export const clearBrowserSessionCookies = () => {
  Cookies.remove(getSessionCookieName(), getSessionCookieAttributes());
  Cookies.remove('auth_data');
  Cookies.remove('user');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const deleteIndexedDBOnLogout = async () => {
  const dbs = await window.indexedDB.databases();
  dbs.forEach(db => {
    window.indexedDB.deleteDatabase(db.name);
  });
};

export const clearCookiesOnLogout = () => {
  window.bus.$emit(CHATWOOT_RESET);
  window.bus.$emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || '/';
  window.location = logoutRedirectLink;
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (typeof error === 'string') {
    return error;
  }
  return error?.message || null;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};
